import { GQLOrderDirection } from '@theprintbar/graphql';
import { getSearchObjectFromParams } from '@theprintbar/shared';
import { z } from 'zod';
import { defaultItemsPerPageOptions } from './Pagination';

export const sortField = () => z.nativeEnum(GQLOrderDirection);

export const baseTableKeys = {
	Page: 'page',
	ItemsPerPage: 'itemsPerPage',
} as const;

export type BaseTableKeys = (typeof baseTableKeys)[keyof typeof baseTableKeys];

export const baseTableSchema = z.object({
	[baseTableKeys.Page]: z.coerce.number().min(1).optional(),
	[baseTableKeys.ItemsPerPage]: z.coerce.number().min(1).optional(),
});

export type BaseTableSearchQuery = z.infer<typeof baseTableSchema>;

export interface GetTableSearchUrlConfig<Q extends BaseTableSearchQuery> {
	searchKey?: string;
	searchValue?: string;
	param: keyof Q;
	value: any;
	currentQuery: Q;
	isArray?: boolean;
	custom?: {
		check: (config: Omit<GetTableSearchUrlConfig<Q>, 'customCheck' | 'customAction'>) => boolean;
		action: (params: URLSearchParams, config: Omit<GetTableSearchUrlConfig<Q>, 'customCheck' | 'customAction'>) => URLSearchParams;
	};
}

export function getBaseTableFilterQuery(params: BaseTableSearchQuery, defaultItemsPerPage?: number) {
	const tableSize = params.itemsPerPage || defaultItemsPerPage || defaultItemsPerPageOptions[0];
	return {
		take: tableSize,
		skip: params.page ? (params.page - 1) * tableSize : 0,
	};
}

export function getTableSearchURL<Q extends BaseTableSearchQuery>(config: GetTableSearchUrlConfig<Q>) {
	const { param, value, isArray, custom, currentQuery, searchValue, searchKey } = config;

	let params = new URLSearchParams();
	// Set params from the last query that have a non-default value
	Object.entries(currentQuery).map(([key, value]) => {
		params.set(key, typeof value !== 'string' ? JSON.stringify(value) : value);
	});

	if (typeof param !== 'string') return '';

	// Reset the page if we're changing the search
	if (param !== baseTableKeys.Page) params.delete(baseTableKeys.Page);

	if (!value) {
		// Passing an empty value means we want to clear the key
		params.delete(param);
	} else if (custom?.check(config)) {
		params = custom?.action(params, config);
	} else if (isArray) {
		const current = currentQuery[param] as string[] | undefined;
		// If there are existing category filters, e.g: category=['shirts', 'hoodies']
		if (!!current && !!current.length) {
			// And this category is already in the list
			if (current.includes(value)) {
				//Remove the whole thing if it's the only one
				if (current.length === 1) {
					params.delete(param);
					// Otherwise filter this one out of the list
				} else {
					params.set(param, JSON.stringify(current.filter((c: string) => c !== value)));
				}
			} else {
				// Otherwise add it to the list
				params.set(param, JSON.stringify([...current, value]));
			}
		} else {
			// Otherwise just set it
			params.set(param, JSON.stringify([value]));
		}
	} else {
		if (!currentQuery[param] || currentQuery[param] !== value) {
			params.set(param, value);

			//We can only sort one column at a time so if the value is a GQLOrderDirection, remove any other params that are also GQLOrderDirection
			if (Object.values(GQLOrderDirection).includes(value)) {
				params.forEach((pValue, key) => {
					if (key !== param && Object.values(GQLOrderDirection).includes(pValue as GQLOrderDirection)) {
						params.delete(key);
					}
				});
			}

			// Otherwise remove it
		} else {
			params.delete(param);
		}
	}

	if (searchKey) {
		//The searchValue is a series of key value pairs ?searchKey1=searchValue1&searchKey2=searchValue2
		//We need to replace the searchKey with the new params serialised as a string
		const searchParams = new URLSearchParams(searchValue);
		searchParams.set(searchKey, JSON.stringify(getSearchObjectFromParams(params)));
		return `?${decodeURIComponent(searchParams.toString())}`;
	}

	return `?${decodeURIComponent(params.toString())}`;
}
